import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';

import { AuthService } from '@auth0/auth0-angular';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, timeout } from 'rxjs/operators';

import { PermissionService } from './permission.service';
import { AppSelectors } from '../../../store/app/app.selectors';
import { UserProfileSelectors } from '../../../store/user-profile/user-profile.selectors';
import type { AssetWorkspace } from '../../home/models/asset-workspace.model';
import { UserProfileService } from '../../settings/services/user-profile.service';
import { isEmbedded } from '../constants/embedded.regex';
import { SemerisRoutes } from '../constants/routes';
import { AccountStatus, resolveAccountStatus } from '../models/account-status.model';
import { wsLink } from '../utils/workspace-link';

/** Cap on the profile wait, so a failed boot cannot hang the router. */
const PROFILE_WAIT_TIMEOUT_MS = 10_000;

/** The status page an account is confined to, or `null` when it may use the app. */
export function accountStatusRedirect(router: Router, status: AccountStatus): UrlTree | null {
  if (status === 'blocked') {
    return router.createUrlTree(['/', SemerisRoutes.blocked]);
  }

  return status === 'unverified' || status === 'pending' ? router.createUrlTree(['/', SemerisRoutes.welcome]) : null;
}

/**
 * Sends blocked accounts to `/blocked` and accounts without access to `/welcome`. Decides from the
 * loaded profile alone: those accounts may never finish the app init that later guards wait on.
 */
export const accountStatusGuard: CanActivateFn = () => {
  if (isEmbedded()) {
    return true;
  }

  const router = inject(Router);

  return currentAccountStatus().pipe(map((status) => (status ? accountStatusRedirect(router, status) : null) ?? true));
};

/**
 * Whether the account has a workspace to open: its tenant licenses assets, and the selected
 * workspace either covers some or is new-style, which legitimately covers none.
 */
export function hasWorkspaceToWorkIn(
  assetCount: number,
  assetShortNames: readonly string[] | undefined,
  workspace: Pick<AssetWorkspace, 'legacy'> | undefined,
): boolean {
  return assetCount > 0 && (!!assetShortNames?.length || workspace?.legacy === false);
}

/** Keeps `/welcome` for accounts without access, including ones with permissions but no workspace to work in. */
export const welcomeGuard: CanActivateFn = () => {
  const router = inject(Router);
  const store = inject(Store);

  return currentAccountStatus().pipe(
    switchMap((status) => {
      if (status === 'blocked') {
        return of(accountStatusRedirect(router, status));
      }

      if (status !== 'onboarding' && status !== 'active') {
        return of(true);
      }

      // `AssetGuard` sends an account without assets here, so sending it home would loop.
      return combineLatest([
        store.select(AppSelectors.assets),
        store.select(UserProfileSelectors.assetShortNames),
        store.select(AppSelectors.selectedWorkspace),
      ]).pipe(
        filter(([assets]) => !!assets),
        take(1),
        map(([assets, assetShortNames, workspace]) =>
          hasWorkspaceToWorkIn(assets.length, assetShortNames, workspace) ? router.createUrlTree(wsLink(SemerisRoutes.home.root)) : true,
        ),
      );
    }),
  );
};

/** Keeps `/blocked` for blocked accounts; anyone else goes where their status sends them, or home. */
export const blockedGuard: CanActivateFn = () => {
  const router = inject(Router);

  // A fresh profile, like `AssetGuard`'s: with the boot one, a block since boot would bounce between them.
  return inject(UserProfileService)
    .getFreshOrLoadedProfile()
    .pipe(
      map((profile) => {
        const status = resolveAccountStatus(profile);

        return status === 'blocked'
          ? true
          : (accountStatusRedirect(router, status) ?? router.createUrlTree(wsLink(SemerisRoutes.home.root)));
      }),
      catchError(() => of(false)),
    );
};

/** `null` when signed out, which `AuthGuard` handles, or when the profile never arrives. */
function currentAccountStatus(): Observable<AccountStatus | null> {
  const authService = inject(AuthService);
  const permissionService = inject(PermissionService);

  return authService.isAuthenticated$.pipe(
    take(1),
    switchMap((isAuthenticated) =>
      isAuthenticated
        ? permissionService.accountStatus$.pipe(take(1), timeout({ first: PROFILE_WAIT_TIMEOUT_MS, with: () => of(null) }))
        : of(null),
    ),
  );
}
